import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Skull, ChevronUp, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DoomsdayClock = ({ token }) => {
  const [target, setTarget] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [loaded, setLoaded] = useState(false);

  // Load from backend on mount
  useEffect(() => {
    fetch(`${API_URL}/api/admin/doomsday`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.target != null) {
          setTarget(data.target);
          setRemaining(data.remaining);
        }
        setLoaded(true);
      })
      .catch(() => {
        // Fallback to localStorage
        const t = parseInt(localStorage.getItem('doomsday_target') || '0');
        const r = parseInt(localStorage.getItem('doomsday_remaining') || '0');
        setTarget(t);
        setRemaining(r);
        setLoaded(true);
      });
  }, [token]);

  const persist = useCallback((t, r) => {
    localStorage.setItem('doomsday_target', String(t));
    localStorage.setItem('doomsday_remaining', String(r));
    fetch(`${API_URL}/api/admin/doomsday`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ target: t, remaining: r }),
    }).catch(() => {});
  }, [token]);

  const adjustRemaining = (delta) => {
    const newR = Math.max(0, remaining + delta);
    setRemaining(newR);
    const newT = delta > 0 ? Math.max(target, newR) : target;
    if (newT !== target) setTarget(newT);
    persist(newT, newR);
  };

  const setStartingValue = (val) => {
    const v = Math.max(0, val);
    setTarget(v);
    setRemaining(v);
    persist(v, v);
  };

  const decrementNo = useCallback(() => {
    setRemaining(prev => {
      const newR = Math.max(0, prev - 1);
      setTarget(t => {
        persist(t, newR);
        return t;
      });
      return newR;
    });
  }, [persist]);

  const reset = () => {
    setRemaining(target);
    persist(target, target);
  };

  // Expose for CallingMode "Not Interested" button
  useEffect(() => {
    window.__doomsdayDecrement = decrementNo;
    return () => { delete window.__doomsdayDecrement; };
  }, [decrementNo]);

  const pct = target > 0 ? ((target - remaining) / target) * 100 : 0;
  const isZero = remaining === 0 && target > 0;
  const urgentColor = remaining <= 5 && target > 0 ? '#FF3B30' : remaining <= Math.ceil(target * 0.25) ? '#FF8C00' : '#FFD700';
  const digits = String(remaining).padStart(3, '0').split('');

  return (
    <div data-testid="doomsday-clock" style={{
      background: '#0a0a0a', border: '2px solid #FF3B30', borderRadius: '10px',
      padding: '0.75rem 1rem', marginBottom: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <Skull size={22} color="#FF3B30" />
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontFamily: "'Bangers', cursive", fontSize: '0.95rem', color: '#FF3B30', letterSpacing: '0.03em' }}>Doomsday Clock</div>
          <div style={{ fontSize: '0.6rem', color: '#666', fontWeight: 600 }}>No's until breakthrough</div>
        </div>

        {/* Up/Down Arrows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <button data-testid="doomsday-up" onClick={() => adjustRemaining(1)}
            style={{ width: '28px', height: '20px', borderRadius: '4px', background: '#111', border: '1.5px solid #FF3B3050', color: '#FF3B30', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <ChevronUp size={14} />
          </button>
          <button data-testid="doomsday-down" onClick={() => adjustRemaining(-1)}
            style={{ width: '28px', height: '20px', borderRadius: '4px', background: '#111', border: '1.5px solid #FF3B3050', color: '#FF3B30', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <ChevronDown size={14} />
          </button>
        </div>

        {/* Giant Counter */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {digits.map((d, i) => (
            <div key={i} style={{
              fontFamily: "'Bangers', cursive", fontSize: '2.75rem', lineHeight: 1,
              color: isZero ? '#22c55e' : urgentColor,
              background: '#111', border: `2px solid ${isZero ? '#22c55e' : urgentColor}40`,
              borderRadius: '6px', padding: '0 0.4rem', minWidth: '38px', textAlign: 'center',
              textShadow: isZero ? '0 0 20px #22c55e' : `0 0 12px ${urgentColor}40`,
            }}>{d}</div>
          ))}
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: '90px' }}>
          <div style={{ width: '90px', height: '6px', background: '#222', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', borderRadius: '3px', background: isZero ? '#22c55e' : urgentColor, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontSize: '0.6rem', fontWeight: 800, color: isZero ? '#22c55e' : '#888' }}>
            {isZero ? 'BREAKTHROUGH!' : `${target - remaining}/${target}`}
          </span>
        </div>

        {/* Set Starting Value */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto' }}>
          <input
            data-testid="doomsday-input"
            type="number" min={1} placeholder="No's"
            onKeyDown={e => { if (e.key === 'Enter') { setStartingValue(parseInt(e.target.value) || 0); e.target.value = ''; } }}
            style={{ width: '70px', padding: '4px 6px', background: '#111', border: '1.5px solid #FF3B30', borderRadius: '5px', color: '#FF3B30', fontSize: '0.85rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }}
          />
          <button data-testid="doomsday-set" onClick={() => {
            const inp = document.querySelector('[data-testid="doomsday-input"]');
            if (inp) { setStartingValue(parseInt(inp.value) || 0); inp.value = ''; }
          }}
            style={{ padding: '4px 10px', background: '#FF3B30', color: '#fff', border: '1.5px solid #000', borderRadius: '5px', fontWeight: 800, fontSize: '0.65rem', cursor: 'pointer', textTransform: 'uppercase' }}>
            Set
          </button>
          <button data-testid="doomsday-reset" onClick={reset}
            style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', padding: '4px 8px', background: 'none', border: '1.5px solid #333', borderRadius: '5px', color: '#888', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700 }}>
            <RotateCcw size={11} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};
